import { Card } from "@components/shadcn-ui/card";
import { useIsMobile } from "@customHooks/use-mobile";
import { useDynamicHeight } from "@customHooks/useDynamicHeight";
import { toast } from "@customHooks/useToast";
import { applicationService } from "features/createApplication/service/applicationService";
import { ApplicationStepper } from "features/createApplication/components/ApplicationStepper";
import { useMultiStepForm } from "features/loanApplications/context/MultiStepFormContext";
import { loanFormFields } from "features/loanApplications/formFieldsConfig";
import { MultiStepLoanApplication } from "features/loanApplications/MultiStepForm";
import { useEffect, useState } from "react";
import { Link, useLocation, useParams, useSearchParams } from "react-router-dom";
import { decodeURIComponent, encodeURIComponent } from "@lib/utils";
import { getProductTypeByValue, PRODUCT_TYPES } from "features/applications/config";
import PermissionWrapper from "features/Permission/PermissionWrapper";
import { Action, ApplicationState } from "features/Permission/types";
import Snipper from "@components/ui/Snipper";
import { ArrowLeft, Users } from "lucide-react";
import { GROUP_DETAILS } from "features/loanApplications/formFieldsConfig/groupDetails";

const FallBackUI = () => {
  return (
    <p className="flex w-full h-full gap-4 justify-center items-center">
      <Snipper />
      Checking permissions...
    </p>
  );
};

const DontHavePermission = () => {
  return (
    <p className="flex w-full h-full gap-4 justify-center items-center">
      You don't have permission to edit the applications.
    </p>
  );
};

const showToast = (variant: "default" | "destructive", description: string) => {
  toast({
    variant,
    description,
  });
};

function Header({ action }: { action: string | null }) {
  return (
    <strong className="block mt-0 text-md bg-[#bbd0ce] p-2 pl-4 rounded-sm">
      {action === "edit" ? "Edit Application" : "Create Application"}
    </strong>
  );
}

function StepContent({
  currentStep,
  isMobile,
  formFileds,
  goToStep,
  handleAction,
  fieldDisableState,
  applicationSatus,
  productType,
}: {
  applicationFormSteps: any;
  currentStep: any;
  isMobile: any;
  formFileds: any;
  goToStep: any;
  handleAction: any;
  fieldDisableState: any;
  applicationSatus: any;
  productType: any;
}) {
  return (
    <>
      <MultiStepLoanApplication
        goToStep={goToStep}
        currentStep={currentStep}
        formFileds={formFileds}
        handleAction={handleAction}
        fieldDisableState={fieldDisableState}
        isMobile={isMobile}
        applicationStatus={applicationSatus}
        productType={productType}
      />
    </>
  );
}

function ApplicationFormContainer({
  applicationFormSteps,
  currentStep,
  elementRef,
  goToStep,
  formFileds,
  handleAction,
  fieldDisableState,
  applicationSatus,
  productType,
}: any) {
  const isMobile = useIsMobile();

  return (
    <div
      className="flex flex-col py-0 scrollbar-thin scrollbar-thumb-gray-500 scrollbar-track-gray-100"
      ref={elementRef}
      style={{ height: "calc(100% - 40px)" }}
    >
      {!isMobile && (
        <div className="flex justify-center sticky top-0 m-auto my-8">
          <ApplicationStepper
            stepsDetails={applicationFormSteps}
            onStepChange={goToStep}
            currentStep={currentStep}
          />
        </div>
      )}
      <div className="flex-1 flex flex-col overflow-auto px-2">
        <StepContent
          applicationFormSteps={applicationFormSteps}
          currentStep={currentStep}
          isMobile={isMobile}
          formFileds={formFileds}
          goToStep={goToStep}
          fieldDisableState={fieldDisableState}
          handleAction={handleAction}
          applicationSatus={applicationSatus}
          productType={productType}
        />
      </div>
    </div>
  );
}

function CreateApplicationPage() {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const { currentStep, goToNextStep, goToStep, setLeadId } = useMultiStepForm();
  const { ref: elementRef, height } = useDynamicHeight();
  const { leadId } = useParams();
  const decodedLeadId = leadId || "";
  const action = searchParams.get("action");
  const isEditMode = action === "edit";
  const id = searchParams.get("id");
  const [applicationFormFields, setApplicationFormFields] =
    useState(loanFormFields);
  const [fetchedApplicationDetails, setFetchedApplicationDetails] = useState<{
    id?: string;
    leadId?: string;
    state?: ApplicationState;
    productType?: string;
    groupDetails?: {
      city?: string;
      id?: string;
      name?: string;
    };
  }>({});

  // Store leadId in context for use in address form
  useEffect(() => {
    if (decodedLeadId) {
      setLeadId(decodedLeadId);
      console.log("Setting lead ID in context:", decodedLeadId);
    }
  }, [decodedLeadId, setLeadId]);

  // If we're in edit mode and have an ID, also set it in the form
  useEffect(() => {
    if (isEditMode && fetchedApplicationDetails.leadId) {
      setLeadId(fetchedApplicationDetails.leadId);
      console.log("Setting lead ID from fetched details:", fetchedApplicationDetails.leadId);
    }
  }, [isEditMode, fetchedApplicationDetails.leadId, setLeadId]);

  useEffect(() => {
    const fetchFormDetails = async () => {
      try {
        const data = await applicationService.getApplicationById(id!);
        setFetchedApplicationDetails(data);

        let currentFormFields = [...loanFormFields];
        if (data.productType === PRODUCT_TYPES.GROUP_LOAN) {
          const personalDetailsIndex = currentFormFields.findIndex(s => s.name === "personalDetails");
          if (personalDetailsIndex !== -1) {
            const groupDetailsSection = {
              sectionDescription: "Group Details",
              section: "Group Details",
              name: "groupDetails",
              fields: GROUP_DETAILS.fields,
              icon: Users
            };
            // Insert Group Details after Personal Details
            currentFormFields.splice(personalDetailsIndex + 1, 0, groupDetailsSection as any);
          }
        }

        const updatedFormData = currentFormFields.map((section: any) => ({
          ...section,
          fields: section.fields.map((field: any) => {
            let defaultValue =
              data[section.name]?.[field.name] || data[field.name];

            // Map API groupDetails format to form field names
            if (section.name === 'groupDetails' && data.groupDetails) {
              if (field.name === 'groupName') {
                defaultValue = data.groupDetails.name;
              } else if (field.name === 'groupCity') {
                defaultValue = data.groupDetails.city;
              } else if (field.name === 'groupId') {
                defaultValue = data.groupDetails.id;
              }
            }

            // Auto-fill PIL Member Name from Personal Details
            if (section.name === 'membershipDetails' && field.name === 'name') {
              const firstName = data.firstName || data.personalDetails?.firstName || "";
              const lastName = data.lastName || data.personalDetails?.lastName || "";
              const autoFilledName = `${firstName} ${lastName}`.trim();
              defaultValue = defaultValue || autoFilledName;
            }

            // Check if the field type is 'productType' and modify the default value accordingly
            if (field.name === "productType") {
              defaultValue =
                getProductTypeByValue(defaultValue) || defaultValue;
            }

            return {
              ...field,
              defaultValue,
            };
          }),
        }));

        setApplicationFormFields(updatedFormData);
        console.log("updatedFormData", updatedFormData);
      } catch (error) {
        console.error("Error fetching application details:", error);
      }
    };

    // Handle initialization from Group Details page navigation state
    const initializeFromLocationState = () => {
      const locationState = location.state as any;
      if (locationState?.groupDetails && locationState?.productType === PRODUCT_TYPES.GROUP_LOAN) {
        console.log("Initializing from location state:", locationState);

        let currentFormFields = [...loanFormFields];
        const personalDetailsIndex = currentFormFields.findIndex(s => s.name === "personalDetails");
        if (personalDetailsIndex !== -1) {
          const groupDetailsSection = {
            sectionDescription: "Group Details",
            section: "Group Details",
            name: "groupDetails",
            fields: GROUP_DETAILS.fields,
            icon: Users
          };
          // Insert Group Details after Personal Details
          currentFormFields.splice(personalDetailsIndex + 1, 0, groupDetailsSection as any);
        }

        const updatedFormData = currentFormFields.map((section: any) => ({
          ...section,
          fields: section.fields.map((field: any) => {
            let defaultValue = field.defaultValue;

            // Prefill group details from location state
            // Map from API format (name, city, id) to form field names (groupName, groupCity, groupId)
            if (section.name === "groupDetails") {
              if (field.name === "groupName") {
                defaultValue = locationState.groupDetails.name;
              } else if (field.name === "groupCity") {
                defaultValue = locationState.groupDetails.city;
              } else if (field.name === "groupId") {
                defaultValue = locationState.groupDetails.id;
              }
            }

            // Prefill product type
            if (field.name === "productType") {
              defaultValue = getProductTypeByValue(PRODUCT_TYPES.GROUP_LOAN) || "Group Loan";
            }

            // Prefill Application ID
            if (section.name === "applicationDetails" && field.name === "id") {
              defaultValue = id;
            }

            return {
              ...field,
              defaultValue,
            };
          }),
        }));

        setApplicationFormFields(updatedFormData);

        // Log the fields for debugging
        const groupDetailsSection = updatedFormData.find((s: any) => s.name === "groupDetails");
        console.log("Group Details Section after setting:", groupDetailsSection);
        console.log("Group Details Fields:", groupDetailsSection?.fields);

        setFetchedApplicationDetails({
          id: id!,
          productType: PRODUCT_TYPES.GROUP_LOAN,
          groupDetails: locationState.groupDetails
        });
      }
    };

    // Check if we have location state (coming from Group Details page)
    // This happens when creating a new app, even though action=edit
    const hasLocationState = location.state && (location.state as any).groupDetails;

    console.log("=== CreateApplicationPage useEffect Debug ===");
    console.log("isEditMode:", isEditMode);
    console.log("id:", id);
    console.log("location.state:", location.state);
    console.log("hasLocationState:", hasLocationState);

    if (isEditMode && id && !hasLocationState) {
      // Editing an existing application - fetch from backend
      fetchFormDetails();
    } else if (hasLocationState) {
      // New application from Group Details - use location state
      initializeFromLocationState();
    }
  }, [isEditMode, id, location.state]);

  // Generic function to perform the action and handle the response
  const performApplicationAction = async (
    formData: any,
    actionType: string,
    leadId?: string
  ) => {
    try {
      await applicationService.performAction(formData, actionType, leadId);

      // Success toast message
      let successMessage = "";
      if (actionType === "SAVE_APPLICATION") {
        successMessage = "Data Saved Successfully!";
      } else if (actionType === "CREATE_APPLICATION") {
        successMessage = `You have initiated an application successfully..!`;
      } else if (actionType === "SUBMIT_APPLICATION") {
        successMessage = "Application has been submitted Successfully!";
      }

      showToast("default", successMessage);
      goToNextStep();
    } catch (error) {
      const errorMessage =
        actionType === "CREATE_APPLICATION"
          ? "Something went wrong... could not initiate application"
          : "Something went wrong... Please try again";
      showToast("destructive", errorMessage);
      console.error(`Error in ${actionType.toLowerCase()}:`, error);
    }
  };

  const saveApplicationForm = async ({ formData }: any) => {
    performApplicationAction(formData, "SAVE_APPLICATION");
  };

  const handleApplicationInitialization = async ({ formData }: any) => {
    performApplicationAction(formData, "CREATE_APPLICATION", decodedLeadId);
  };

  const submitApplicationForm = async ({ formData }: any) => {
    performApplicationAction(formData, "SUBMIT_APPLICATION");
  };

  const handleAction = (actionType: string, formData: Object) => {
    console.log("Action type:", actionType, "Form data:", formData);

    // For Group Loans, map form field names to API format and ensure groupDetails is included
    let enhancedFormData = { ...formData };
    if (fetchedApplicationDetails?.productType === PRODUCT_TYPES.GROUP_LOAN) {
      const formDataAny = enhancedFormData as any;
      const locationState = location.state as any;
      const groupDetailsFromState = locationState?.groupDetails;
      const groupDetailsFromFetched = fetchedApplicationDetails?.groupDetails;

      // Map form field names (groupName, groupCity, groupId) to API format (name, city, id)
      const groupDetails = {
        name: formDataAny.groupName || groupDetailsFromState?.name || groupDetailsFromFetched?.name,
        city: formDataAny.groupCity || groupDetailsFromState?.city || groupDetailsFromFetched?.city,
        id: formDataAny.groupId || groupDetailsFromState?.id || groupDetailsFromFetched?.id || decodedLeadId
      };

      // Remove the form field names and add the mapped groupDetails object
      delete formDataAny.groupName;
      delete formDataAny.groupCity;
      delete formDataAny.groupId;

      enhancedFormData = {
        ...formDataAny,
        groupDetails
      };

      console.log("Enhanced form data with groupDetails:", enhancedFormData);
    }


    // Remove id from payload for CREATE_APPLICATION
    if (actionType === "CREATE_APPLICATION") {
      const formDataAny = enhancedFormData as any;
      if (formDataAny.id) {
        delete formDataAny.id;
      }
      enhancedFormData = { ...formDataAny };
    }

    if (actionType === "SAVE_APPLICATION") {
      saveApplicationForm({ formData: enhancedFormData });
    } else if (actionType === "CREATE_APPLICATION") {
      handleApplicationInitialization({ formData: enhancedFormData });
    } else if (actionType === "SUBMIT_APPLICATION") {
      submitApplicationForm({ formData: enhancedFormData });
    }
  };

  // Filter out Group Loan from product type options when creating a normal application
  useEffect(() => {
    const hasLocationState = location.state && (location.state as any).groupDetails;

    if (!isEditMode && !hasLocationState) {
      setApplicationFormFields(prevFields => {
        return prevFields.map(section => {
          if (section.name === "applicationDetails") {
            return {
              ...section,
              fields: section.fields.map(field => {
                if (field.name === "productType" && field.options) {
                  return {
                    ...field,
                    options: field.options.filter((opt: string) => opt !== "Group Loan")
                  };
                }
                return field;
              })
            };
          }
          return section;
        });
      });
    }
  }, [isEditMode, location.state]);

  const fieldDisableState = {
    id: true,
    productType: true,
    // Disable group-specific fields - these are unique and won't affect other sections
    groupName: fetchedApplicationDetails?.productType === PRODUCT_TYPES.GROUP_LOAN,
    groupCity: fetchedApplicationDetails?.productType === PRODUCT_TYPES.GROUP_LOAN,
  };

  const applicationFormSteps = [...applicationFormFields];

  return (
    <>
      <Link
        to={
          fetchedApplicationDetails?.productType === PRODUCT_TYPES.GROUP_LOAN &&
            fetchedApplicationDetails?.leadId === fetchedApplicationDetails?.groupDetails?.id
            ? `/dashboard/groups/${encodeURIComponent(fetchedApplicationDetails?.leadId ?? "")}?city=${encodeURIComponent(fetchedApplicationDetails?.groupDetails?.city ?? "")}`
            : `/dashboard/lead-details/${encodeURIComponent(fetchedApplicationDetails?.leadId ?? "")}`
        }
        className="flex w-fit items-center gap-2 p-2 bg-slate-300 mb-1 rounded-md"
      >
        <ArrowLeft /> {fetchedApplicationDetails?.productType === PRODUCT_TYPES.GROUP_LOAN ? "Back to Group details" : "Back to Lead details page"}
      </Link>
      <Card className="relative h-full">
        <PermissionWrapper
          actions={[Action.EDIT_APPLICATION, Action.SUBMIT_APPLICATION]}
          fallback={<FallBackUI />}
        >
          {({ isAllowed }) =>
            isAllowed ? (
              <>
                <Header action={action} />
                <ApplicationFormContainer
                  applicationFormSteps={applicationFormSteps}
                  currentStep={currentStep}
                  id={id}
                  elementRef={elementRef}
                  height={height}
                  formFileds={applicationFormFields}
                  goToStep={goToStep}
                  fieldDisableState={fieldDisableState}
                  handleAction={handleAction}
                  applicationSatus={fetchedApplicationDetails.state}
                  productType={fetchedApplicationDetails.productType}
                />
              </>
            ) : (
              <DontHavePermission />
            )
          }
        </PermissionWrapper>
      </Card>
    </>
  );
}

export default CreateApplicationPage;
